import Model, { attr } from '@ember-data/model';
import type { Type } from '@warp-drive/core-types/symbols';

export default class IntegrationSettingModel extends Model {
  declare [Type]: 'integration-setting';
  @attr('string') declare provider_name: string;

  /**
   * An external identifier, not a status field — it is the sort key of GSI1
   * (`GSK1: "${provider_id}"`), so it has to hold something real. It starts as
   * `'pending'` and becomes the GitLab user id once the repo-list job
   * validates the credential, exactly mirroring GitHub's `'pending'` →
   * `installation_id`. Job progress lives in `repos_status`, not here.
   */
  @attr('string') declare provider_id: string;

  @attr('string') declare integration_person: string;

  @attr declare repository_count: number | null;
  @attr declare repository_selection: string | null; // 'all' or 'selected'

  /**
   * Both fields are written by the provider's install callback, so a record can
   * exist without them. `!= null` rather than a truthy check so a real count of
   * zero still renders.
   */
  get hasRepositoryCount() {
    return this.repository_count != null;
  }

  get repositorySelectionKey() {
    if (this.repository_selection == null) return null;
    // Anything the backend sends that isn't 'all' means a hand-picked subset.
    const suffix = this.repository_selection === 'all' ? 'all' : 'selected';
    return `scans.new.repository-selection-${suffix}`;
  }

  /**
   * Display only. The authoritative copy of the instance URL is the one sealed
   * inside the credential blob — that is what clone-consumer derives the git
   * host from. The two are not interchangeable.
   */
  @attr('string') declare instance_url: string;

  /** `FETCHING` | `READY` | `FAILED`. `FETCHING` is the only non-terminal one. */
  @attr('string') declare repos_status: string;

  @attr('date') declare repos_fetched_at: Date | null;

  @attr('string') declare repos_error: string | null;

  /**
   * `PENDING` → `STORED` | `FAILED`, set by `storeCredential` (clone-consumer)
   * via the integration-settings callback. The POST that creates this record
   * only ever returns `202` — connecting is asynchronous from the start, not
   * just for the repo list.
   */
  @attr('string') declare credential_status: string;

  /** Cached repo list, written by the async list job. */
  @attr() declare repos: unknown;

  /** User-named group/project paths — see `connectWithCredentials`. */
  @attr() declare group_paths: string[] | null;
}
